// src/i18n/es/reactAdmin.ts
//
// The framework's own messages in Spanish. The Spanish package this app
// depends on predates the framework version in use: about ninety of its keys
// are missing and a few are left in English (the «Not found» page text, the
// pagination labels), so the user met English on an otherwise Spanish
// screen. This file completes it; index.ts still layers its own wording on
// top.
import raSpanishMessages from 'ra-language-spanish';

const complement = {
  action: {
    clear_array_input: 'Vaciar la lista',
    clear_input_value: 'Borrar el valor',
    clone: 'Duplicar',
    confirm: 'Confirmar',
    create_item: 'Crear %{item}',
    export: 'Exportar',
    remove_all_filters: 'Quitar todos los filtros',
    search: 'Buscar',
    search_columns: 'Buscar columnas',
    select_all: 'Seleccionar todo',
    select_all_button: 'Seleccionar todo',
    select_row: 'Seleccionar esta fila',
    unselect: 'Deseleccionar',
    expand: 'Expandir',
    close: 'Cerrar',
    open_menu: 'Abrir el menú',
    close_menu: 'Cerrar el menú',
    update: 'Actualizar',
    move_up: 'Subir',
    move_down: 'Bajar',
    open: 'Abrir',
    toggle_theme: 'Cambiar entre modo claro y oscuro',
    select_columns: 'Columnas',
    update_application: 'Recargar la aplicación',
  },
  boolean: {
    false: 'No',
    null: ' ',
  },
  page: {
    dashboard: 'Inicio',
    error: 'Algo salió mal',
    empty: 'Todavía no hay %{name}.',
    invite: '¿Desea añadir uno?',
    not_found: 'Página no encontrada',
    access_denied: 'Acceso denegado',
    authentication_error: 'Error de autenticación',
  },
  input: {
    password: {
      toggle_visible: 'Ocultar la contraseña',
      toggle_hidden: 'Mostrar la contraseña',
    },
  },
  message: {
    access_denied: 'No tiene permiso para acceder a esta página.',
    authentication_error: 'No se han podido comprobar sus credenciales. Inténtelo de nuevo.',
    auth_error: 'No se ha podido validar su sesión.',
    bulk_update_content:
      '¿Está seguro de que desea actualizar %{name} %{recordRepresentation}? |||| ¿Está seguro de que desea actualizar estos %{smart_count} elementos?',
    bulk_update_title: 'Actualizar %{name} %{recordRepresentation} |||| Actualizar %{smart_count} %{name}',
    clear_array_input: '¿Está seguro de que desea vaciar toda la lista?',
    details: 'Detalles',
    error: 'Ha ocurrido un error y no se ha podido completar la solicitud.',
    no: 'No',
    // The not-found page adds the final full stop itself.
    not_found: 'La dirección no existe o el enlace que ha seguido no es correcto',
    select_all_limit_reached: 'Hay demasiados elementos para seleccionarlos todos. Solo se han seleccionado los primeros %{max}.',
    unsaved_changes: 'Algunos cambios no se han guardado. ¿Desea descartarlos?',
    placeholder_data_warning: 'Problema de conexión: no se han podido actualizar los datos.',
  },
  navigation: {
    clear_filters: 'Quitar los filtros',
    no_filtered_results: 'No hay %{name} con los filtros actuales.',
    partial_page_range_info: '%{offsetBegin}-%{offsetEnd} de más de %{offsetEnd}',
    current_page: 'Página %{page}',
    page: 'Ir a la página %{page}',
    first: 'Ir a la primera página',
    last: 'Ir a la última página',
    next: 'Ir a la página siguiente',
    previous: 'Ir a la página anterior',
    page_rows_per_page: 'Filas por página:',
    skip_nav: 'Ir al contenido principal',
  },
  sort: {
    sort_by: 'Ordenar por %{field_lower_first} %{order}',
    ASC: 'ascendente',
    DESC: 'descendente',
  },
  auth: {
    auth_check_error: 'Inicie sesión para continuar',
    user_menu: 'Perfil',
    email: 'Correo electrónico',
  },
  notification: {
    data_provider_error: 'No se han podido cargar los datos. Inténtelo de nuevo.',
    i18n_error: 'No se han podido cargar los textos del idioma seleccionado',
    logged_out: 'Su sesión ha terminado. Vuelva a iniciar sesión.',
    not_authorized: 'No tiene permiso para acceder a este recurso.',
    application_update_available: 'Hay una versión nueva disponible.',
    offline: 'Sin conexión. No se han podido cargar los datos.',
  },
  validation: {
    unique: 'Debe ser único',
  },
  saved_queries: {
    label: 'Consultas guardadas',
    query_name: 'Nombre de la consulta',
    new_label: 'Guardar la consulta actual…',
    new_dialog_title: 'Guardar la consulta actual como',
    remove_label: 'Eliminar la consulta guardada',
    remove_label_with_name: 'Eliminar la consulta «%{name}»',
    remove_dialog_title: '¿Eliminar la consulta guardada?',
    remove_message: '¿Está seguro de que desea eliminar esta consulta de su lista?',
    help: 'Filtre la lista y guarde esta consulta para más tarde',
  },
  configurable: {
    customize: 'Personalizar',
    configureMode: 'Configurar esta página',
    inspector: {
      title: 'Inspector',
      content: 'Pase el cursor sobre los elementos de la página para configurarlos',
      reset: 'Restablecer la configuración',
      hideAll: 'Ocultar todo',
      showAll: 'Mostrar todo',
    },
    Datagrid: {
      title: 'Tabla',
      unlabeled: 'Columna sin nombre n.º %{column}',
    },
    SimpleForm: {
      title: 'Formulario',
      unlabeled: 'Campo sin nombre n.º %{input}',
    },
    SimpleList: {
      title: 'Lista',
      primaryText: 'Texto principal',
      secondaryText: 'Texto secundario',
      tertiaryText: 'Texto terciario',
    },
  },
};

type Messages = { [key: string]: string | Messages };

const mergeMessages = (base: Messages, extra: Messages): Messages => {
  const out: Messages = { ...base };
  Object.entries(extra).forEach(([key, value]) => {
    const current = out[key];
    out[key] =
      typeof value === 'object' && typeof current === 'object' ? mergeMessages(current, value) : value;
  });
  return out;
};

/** The complete framework message tree (`ra.*`) in Spanish. */
export const raSpanish = mergeMessages(raSpanishMessages.ra as unknown as Messages, complement) as unknown as typeof raSpanishMessages.ra & typeof complement;

export default raSpanish;
